import React, { useMemo, useCallback } from 'react';
import ReactSelect, {
  Props as ReactSelectProps,
  SingleValue,
  MultiValue,
} from 'react-select';

export interface OptionType {
  value: string | number;
  label: string;
}

interface SelectProps
  extends Omit<ReactSelectProps<OptionType, boolean>, 'onChange' | 'value'> {
  /**
   * Externally provided value(s) as primitive(s).
   */
  value?: string | number | Array<string | number> | null;

  /**
   * Whether multi-select is enabled.
   */
  isMulti?: boolean;

  /**
   * Options to display in the dropdown.
   */
  options: OptionType[];

  /**
   * External callback with string/number or array of them.
   */
  onChange?: (value: string | number | Array<string | number> | null) => void;
}

const Select: React.FC<SelectProps> = ({
  value,
  options,
  onChange = () => {},
  isMulti = false,
  ...props
}) => {
  // Map value keys to OptionType objects for react-select
  const flatOptions = useMemo(
    () => options.reduce<Record<string | number, string>>((res, option) => {
      res[option.value] = option.label;
      return res;
    }, {}),
    [options],
  );

  const selectedValue = useMemo(() => {
    if (!value) return null;

    return isMulti
      ? (value as Array<string | number>).map((key) => ({
        value: key,
        label: flatOptions[key],
      }))
      : {
        value: value as string | number,
        label: flatOptions[value as string | number],
      };
  }, [value, isMulti, flatOptions]);

  const handleChange = useCallback(
    (newValue: MultiValue<OptionType> | SingleValue<OptionType>) => {
      if (isMulti) {
        const selected = (newValue as MultiValue<OptionType>)?.map((v) => v.value);
        onChange(selected);
      } else {
        onChange((newValue as SingleValue<OptionType>)?.value ?? null);
      }
    },
    [isMulti, onChange],
  );

  return (
    <ReactSelect
      {...props}
      isMulti={isMulti}
      value={selectedValue}
      options={options}
      onChange={handleChange}
    />
  );
};

export default Select;
